import { Provider, computed, inject } from '@angular/core';

import { ATTENTION_PROJECTIONS, AttentionProjection } from '@shared/services/attention/attention-projections';
import { AttentionTone } from '@shared/services/attention/attention.models';
import { CapabilityService } from '@shared/services/capability/capability.service';
import { HeldTicketService } from '@shared/services/register/held-ticket.service';
import { ShiftService } from '@shared/services/register/shift.service';

import { RETAIL_DEVICE_TILL } from './retail-device-till';

/** The rail items retail's badges decorate — the pages-config urls of the POS module's children. */
export const RETAIL_RAIL = {
  register: '/provider/app/pos/register',
  tickets: '/provider/app/pos/tickets',
  shift: '/provider/app/pos/shift',
} as const;

/**
 * Retail's rail badge and its wind-down blockers (kz2 M2; the DS's `retail.attention.ts` and
 * `retail.open-work.ts`, RT-28, D270, D435), registered at the root (D158): a blocker is a property
 * of the tenant's till, not of what is on screen.
 *
 * - **Tickets** — the baskets parked on this device's till (the source's `Tickets 3`).
 * - **Open work** — a shift still open is money nobody has counted (`retail.shift`); parked tickets
 *   are goods off the shelf (`retail.tickets`). They are what the pre-flight names before either
 *   screen is switched off, and why Setup's *Solo shop* fit is refused mid-shift.
 *
 * They read the device's own holds and shift for the till this device last opened
 * (`RETAIL_DEVICE_TILL`) — not `RetailStore`, which would pull the whole register into the app's
 * initial chunk.
 */
export function retailAttentionProjections(): readonly AttentionProjection[] {
  const holds = inject(HeldTicketService);
  const shifts = inject(ShiftService);
  const cap = inject(CapabilityService);

  const parked = computed(() => {
    const till = RETAIL_DEVICE_TILL();
    return till ? holds.forRegister(till)().length : 0;
  });
  const session = computed(() => {
    const till = RETAIL_DEVICE_TILL();
    return till ? shifts.current(till)() : null;
  });

  cap.registerOpenWork({
    moduleId: 'retail',
    capKey: 'shift',
    work: computed(() => {
      const s = session();
      return s && s.state === 'open'
        ? {
            kind: 'money' as const,
            n: 1,
            label: `unreconciled shift on ${s.register.loc}-${s.register.reg}`,
            resolve: 'Count the drawer and close the shift.',
          }
        : null;
    }),
  });
  cap.registerOpenWork({
    moduleId: 'retail',
    capKey: 'tickets',
    work: computed(() => {
      const n = parked();
      return n
        ? { kind: 'docs' as const, n, label: n === 1 ? 'ticket on hold' : 'tickets on hold', resolve: 'Resume or void them to finish.' }
        : null;
    }),
  });

  return [
    {
      rule: { id: 'retail.tickets.held', moduleId: 'retail', capKey: 'tickets', railItemId: RETAIL_RAIL.tickets, enabled: true, scope: 'site' },
      count: parked,
      tone: computed<AttentionTone>(() => 'warning'),
    },
  ];
}

/** Register retail's projections at the root (AppModule), so the shell can show them. */
export function provideRetailAttention(): Provider[] {
  return [{ provide: ATTENTION_PROJECTIONS, useValue: retailAttentionProjections, multi: true }];
}
